import { Link } from "react-router-dom";
import { ArrowRight, Building2, Calendar, Clock, FileText, Handshake, MapPin, Megaphone, Trophy, Users } from "lucide-react";
import { SEO } from "@/components/SEO";
import { FAQSection } from "@/components/FAQSection";
import locandinaGeometri from "@/assets/locandina-geometri-2026.jpg";
import heroImg from "@/assets/hero-istituzionale.jpg";
import heroImgWebp from "@/assets/hero-istituzionale.webp";
import heroImgWebpMobile from "@/assets/hero-istituzionale-768.webp";
import busImg from "@/assets/fiaba-day-bus.jpg";
import busImgWebp from "@/assets/fiaba-day-bus.webp";
import piazzaImg from "@/assets/fiabaday-piazza.jpg";

const pillars = [
  { icon: Building2, title: "Area Tecnica", desc: "Mappiamo l'accessibilità di spazi urbani, condomini, strutture turistiche e stabilimenti balneari.", to: "/area-tecnica" },
  { icon: Handshake, title: "Protocolli d'Intesa", desc: "Accordi con Presidenza del Consiglio, Ministeri, Regioni, Università e Ordini professionali.", to: "/protocolli-intesa" },
  { icon: Calendar, title: "FIABA Day", desc: "Giornata Nazionale per l'Abbattimento delle Barriere Architettoniche, ogni prima domenica di ottobre a Roma.", to: "/fiaba-day" },
  { icon: Trophy, title: "Concorsi", desc: '"I Futuri Geometri Progettano l\'Accessibilità" — XIV edizione nel 2026, con CNGeGL.', to: "/concorsi" },
  { icon: FileText, title: "Progettazione Accessibile", desc: "Servizio dedicato per cittadini, condomini e stabilimenti balneari secondo l'Universal Design.", to: "/progettazione-accessibile" },
  { icon: Megaphone, title: "Segnalazioni Barriere", desc: "Nuova banca dati partecipata: segnala una barriera nel tuo territorio.", to: "/segnalazioni" },
];

const stats = [
  { n: "25+", l: "Anni di attività" },
  { n: "XXIV", l: "Edizione FIABA Day 2026" },
  { n: "75%", l: "Bonus Barriere promosso da FIABA" },
  { n: "RUNTS", l: "Ente del Terzo Settore" },
];

const Home = () => (
  <>
    <SEO
      title="FIABA ETS — Fondo Italiano Abbattimento Barriere Architettoniche"
      description="FIABA ETS opera da 25 anni per abbattere ogni tipo di barriera architettonica e culturale, promuovendo accessibilità, inclusione e Universal Design in Italia."
      path="/"
    />

    {/* HERO */}
    <section className="relative bg-hero text-primary-foreground overflow-hidden">
      <div className="container grid lg:grid-cols-2 gap-12 items-center py-20 md:py-28 relative z-10">
        <div>
          <p className="inline-block uppercase tracking-widest text-secondary text-xs font-bold mb-5 border-b-2 border-secondary pb-1">
            Ente del Terzo Settore — dal 2000
          </p>
          <h1 className="heading-xl mb-6">
            Abbattiamo ogni <span className="text-gold">barriera</span>. Costruiamo libertà.
          </h1>
          <p className="text-lg text-primary-foreground/85 mb-8 leading-relaxed max-w-xl">
            FIABA ETS opera da 25 anni per la cultura dell'accessibilità, mettendo
            al centro la persona e promuovendo l'Universal Design su tutto il territorio nazionale.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/chi-siamo" className="inline-flex items-center gap-2 bg-gold px-6 py-3 rounded-md font-semibold text-secondary-foreground shadow-gold hover:opacity-90 transition">
              Scopri chi siamo <ArrowRight size={18} />
            </Link>
            <Link to="/segnalazioni" className="inline-flex items-center gap-2 bg-primary-foreground/10 backdrop-blur border border-primary-foreground/30 px-6 py-3 rounded-md font-semibold hover:bg-primary-foreground/20 transition">
              Segnala una barriera
            </Link>
          </div>
        </div>
        <div className="relative" style={{ aspectRatio: "1280 / 853" }}>
          <picture>
            <source
              type="image/webp"
              srcSet={`${heroImgWebpMobile} 768w, ${heroImgWebp} 1280w`}
              sizes="(max-width: 1023px) 100vw, 50vw"
            />
            <img
              src={heroImg}
              alt="Conferenza istituzionale FIABA ETS con bandiere italiane"
              className="rounded-lg shadow-elegant w-full h-full object-cover"
              width={1280}
              height={853}
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </picture>
          <div className="absolute -bottom-6 -left-6 bg-gold text-secondary-foreground p-5 rounded-lg shadow-elegant max-w-[200px] hidden md:block">
            <p className="font-serif text-2xl font-bold leading-none">25+</p>
            <p className="text-sm font-medium mt-1">anni al servizio dell'accessibilità</p>
          </div>
        </div>
      </div>
    </section>

    {/* NOTIZIA IN PRIMO PIANO */}
    <section className="section-padding bg-subtle">
      <div className="container">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="uppercase tracking-widest text-secondary text-xs font-bold mb-3">In primo piano</p>
          <h2 className="heading-lg text-primary">Cerimonia di Premiazione — XIV Edizione</h2>
        </div>
        <div className="grid lg:grid-cols-2 gap-10 items-center bg-card rounded-lg shadow-elegant border border-border overflow-hidden">
          <a
            href={locandinaGeometri}
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-muted"
          >
            <img
              src={locandinaGeometri}
              alt="Locandina della cerimonia di premiazione del concorso I Futuri Geometri Progettano l'Accessibilità — XIV edizione, 4 giugno 2026 a Roma"
              className="w-full h-full object-contain"
              loading="lazy"
              decoding="async"
            />
          </a>
          <div className="p-8 lg:p-10">
            <span className="inline-block bg-gold text-secondary-foreground text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full mb-4">
              Evento 2026
            </span>
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-primary mb-4">
              I Futuri Geometri Progettano l'Accessibilità
            </h3>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Si terrà a Roma la cerimonia di premiazione della XIV edizione del concorso nazionale
              dedicato agli Istituti CAT, promosso da FIABA ETS in collaborazione con il Consiglio
              Nazionale Geometri e Geometri Laureati.
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3 text-foreground/90">
                <Calendar className="w-5 h-5 text-secondary shrink-0" />
                <span><strong>4 giugno 2026</strong> — ore 11.00</span>
              </li>
              <li className="flex items-center gap-3 text-foreground/90">
                <MapPin className="w-5 h-5 text-secondary shrink-0" />
                <span>Sala Trilussa — Cassa Geometri, Via Luisa di Savoia 17, Roma</span>
              </li>
              <li className="flex items-center gap-3 text-foreground/90">
                <Clock className="w-5 h-5 text-secondary shrink-0" />
                <span>Cerimonia di premiazione — XIV edizione</span>
              </li>
            </ul>
            <Link
              to="/concorsi"
              className="inline-flex items-center gap-2 bg-hero text-primary-foreground px-6 py-3 rounded-md font-semibold shadow-elegant hover:opacity-90 transition"
            >
              Scopri il concorso <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </section>



    {/* STATS */}
    <section className="bg-muted py-12">
      <div className="container grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
        {stats.map((s) => (
          <div key={s.l}>
            <p className="font-serif text-4xl md:text-5xl font-bold text-primary">{s.n}</p>
            <p className="text-sm text-muted-foreground mt-2">{s.l}</p>
          </div>
        ))}
      </div>
    </section>

    {/* CHI SIAMO BREVE */}
    <section className="section-padding">
      <div className="container grid lg:grid-cols-2 gap-12 items-center">
        <picture>
          <source srcSet={busImgWebp} type="image/webp" />
          <img src={busImg} alt="Autobus FIABA Day con bandiere durante manifestazione" className="rounded-lg shadow-elegant w-full" width={1024} height={1538} loading="lazy" decoding="async" />
        </picture>
        <div>
          <p className="uppercase tracking-widest text-secondary text-xs font-bold mb-3">Chi siamo</p>
          <h2 className="heading-lg text-primary mb-5">Un quarto di secolo per l'accessibilità in Italia</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Costituita il 27 luglio 2000, FIABA ETS è iscritta al RUNTS dal novembre 2023
            e ha ottenuto riconoscimento giuridico nel 2025. Promuove l'abbattimento
            delle barriere architettoniche fisiche e culturali a livello nazionale.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-6">
            Nel 2021 ha promosso l'emendamento del <strong>Bonus Barriere 75%</strong>,
            misura cardine per migliaia di interventi di accessibilità in tutto il Paese.
          </p>
          <Link to="/chi-siamo" className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all">
            Scopri la nostra storia <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>

    {/* COSA FACCIAMO */}
    <section className="bg-subtle section-padding">
      <div className="container">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="uppercase tracking-widest text-secondary text-xs font-bold mb-3">Cosa facciamo</p>
          <h2 className="heading-lg text-primary">Le nostre aree di intervento</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pillars.map((p) => (
            <Link
              key={p.title}
              to={p.to}
              className="group bg-card p-7 rounded-lg shadow-soft hover:shadow-elegant transition-all border border-border hover:border-primary/30"
            >
              <div className="w-12 h-12 rounded-full bg-hero text-primary-foreground flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <p.icon size={22} />
              </div>
              <h3 className="font-serif text-xl font-bold text-primary mb-2">{p.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
              <span className="inline-flex items-center gap-1 text-sm text-primary font-semibold mt-4 group-hover:gap-2 transition-all">
                Approfondisci <ArrowRight size={14} />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* FIABA DAY HIGHLIGHT */}
    <section className="relative section-padding overflow-hidden bg-primary text-primary-foreground">
      <div
        className="absolute inset-0 opacity-20 bg-cover bg-center"
        style={{ backgroundImage: `url(${piazzaImg})` }}
        aria-hidden="true"
      />
      <div className="container relative z-10 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <p className="uppercase tracking-widest text-secondary text-xs font-bold mb-3">Evento Nazionale</p>
          <h2 className="heading-lg mb-5">FIABA Day — la giornata dell'accessibilità</h2>
          <p className="text-primary-foreground/85 leading-relaxed mb-6">
            Istituita nel 2023 con direttiva del Presidente del Consiglio dei Ministri,
            si tiene a Roma la prima domenica di ottobre. Nel 2026 la <strong>XXIV edizione</strong>.
          </p>
          <Link to="/fiaba-day" className="inline-flex items-center gap-2 bg-gold px-6 py-3 rounded-md font-semibold text-secondary-foreground hover:opacity-90 transition">
            Scopri il FIABA Day <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>

    <FAQSection
      subtitle="Le risposte alle domande più comuni su FIABA ETS, accessibilità e barriere architettoniche."
      items={[
        { q: "Cos'è FIABA ETS?", a: "FIABA è il Fondo Italiano per l'Abbattimento delle Barriere Architettoniche, Ente del Terzo Settore (RUNTS G14936/2023) che dal 2000 promuove accessibilità, inclusione e Universal Design su tutto il territorio nazionale." },
        { q: "Cos'è il Bonus Barriere 75%?", a: "È la detrazione fiscale del 75% per interventi di eliminazione delle barriere architettoniche, promossa anche grazie all'attività di FIABA. Si applica a rampe, ascensori, servoscala, automazioni e adeguamenti di servizi igienici." },
        { q: "Quando si svolge il FIABA Day?", a: "Il FIABA Day è la Giornata Nazionale per l'Abbattimento delle Barriere Architettoniche, istituita nel 2023 con direttiva del Presidente del Consiglio dei Ministri. Si tiene a Roma la prima domenica di ottobre. Nel 2026 si terrà la XXIV edizione." },
        { q: "Come posso segnalare una barriera architettonica?", a: "Tramite la sezione Segnalazioni del nostro sito puoi indicare la posizione, il tipo di barriera e una descrizione. Le segnalazioni alimentano la nostra banca dati partecipata." },
        { q: "Come diventare socio di FIABA ETS?", a: "Compila il modulo nella sezione Diventa socio e versa la quota associativa annuale. I soci sostengono attivamente le iniziative di FIABA per un'Italia più accessibile." },
        { q: "FIABA fornisce servizi a Comuni e aziende?", a: "Sì. Offriamo MiniPEBA, audit di accessibilità, conformità European Accessibility Act, consulenze su Universal Design e il Bollino FIABA per realtà che si distinguono per impegno sull'accessibilità." },
      ]}
    />

    {/* CTA FINALE */}
    <section className="section-padding">
      <div className="container max-w-4xl text-center">
        <Users className="w-14 h-14 mx-auto text-secondary mb-5" />
        <h2 className="heading-lg text-primary mb-5">Unisciti a chi costruisce un'Italia più accessibile</h2>
        <p className="text-muted-foreground leading-relaxed mb-8 max-w-2xl mx-auto">
          Diventando socio di FIABA sostieni un'Associazione che da oltre 25 anni tutela
          il diritto a una mobilità indipendente e a una vita pienamente accessibile per tutti.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/diventa-socio" className="inline-flex items-center gap-2 bg-hero text-primary-foreground px-7 py-3 rounded-md font-semibold shadow-elegant hover:opacity-90 transition">
            Diventa socio
          </Link>
          <Link to="/contatti" className="inline-flex items-center gap-2 border-2 border-primary text-primary px-7 py-3 rounded-md font-semibold hover:bg-primary hover:text-primary-foreground transition">
            Contattaci <MapPin size={16} />
          </Link>
        </div>
      </div>
    </section>
  </>
);

export default Home;
